import React, { useRef, useState } from 'react'

import { Box, ClassNameMap, CreateCSSProperties, makeAppStyles, Popover, useAppTheme, useBreakpoint } from '@percept/mui'
import { fade } from '@material-ui/core/styles'
import { ParentSize } from '@visx/responsive'
import { scaleLinear } from '@visx/scale'

import { every, flatten, get, intersection } from 'lodash-es'
import { produceKeyedMapping } from '@percept/utils'

import { TypedKeys } from '@percept/types'
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints'


export type BubbleChartDatum<Datum extends object, Key extends string> = {
  key: string
  column: Key
  health: number
  data: Datum 
}

export type AggregatedBubbleChartDatum<Datum extends object, Key extends string> = (
  Omit<BubbleChartDatum<Datum, Key>, 'data'> & { data: Datum[], count: number }
)

export type BubbleChartColumn<Key extends string> = {
  key: Key
  label: React.ReactNode
}

export type BubblePopoverProps<Datum extends object, Key extends string> = {
  data: Datum[]
  key: Key
}

export type BubbleChartProps<Datum extends object, Key extends string> = {
  columns: BubbleChartColumn<Key>[]
  data: BubbleChartDatum<Datum, Key>[]
  cellMargin?: number
  minCellSize?: number
  fontSize?: number
  getPopoverContent?: (props: BubblePopoverProps<Datum, Key>) => React.ReactNode
} & (
  {
    previousData?: never
    datumKey?: never
  } | {
    previousData: BubbleChartDatum<Datum, Key>[]
    datumKey: TypedKeys<Datum, string | number>
  }
)

type BubbleChartRow<Datum extends object, Key extends string> = {
  key: string
  label: React.ReactNode
  health: number
  cells: AggregatedBubbleChartDatum<Datum, Key>[]
}

const rowBounds = [
  { health: 0, scaleHealth: 0, key: '1', label: '' },
  { health: 70, scaleHealth: 0.25, key: '2', label: '' },
  { health: 80, scaleHealth: 0.5, key: '3', label: '' },
  { health: 85, scaleHealth: 1, key: '4', label: '' },
]

const getChartRows = <Datum extends object, Key extends string>(
  columns: BubbleChartColumn<Key>[],
  data: BubbleChartDatum<Datum, Key>[],
): BubbleChartRow<Datum, Key>[] => {
  const columnKeyOrder = columns.map( c => c.key )

  const rowMap = rowBounds.reduce( (acc, bound) => {
    acc[bound.key] = []
    return acc
  }, {} as Record<string, BubbleChartDatum<Datum, Key>[]>)

  for( const d of data ){
    const value = d.health * 100
    rowBounds.forEach( (bound, i) => {
      const flags: boolean[] = [value >= bound.health]
      if( i < (rowBounds.length - 1) ){
        flags.push(value < rowBounds[i + 1].health)
      }
      if( every(flags) ){
        rowMap[bound.key].push(d)
      }
    })
  }

  return rowBounds.map( (rowBound) => {
    const collated = rowMap[rowBound.key].reduce( (acc, cell) => {
      acc[cell.column] = acc[cell.column] || []
      acc[cell.column].push(cell)
      return acc
    }, {} as Record<Key, BubbleChartDatum<Datum, Key>[]>)
    return {
      ...rowBound,
      health: rowBound.scaleHealth,
      cells: columnKeyOrder.map( column => {
        const matchingData = collated[column]
        return {
          key: column,
          column,
          count: matchingData ? matchingData.length : 0,
          data: matchingData ? matchingData.map( c => c.data ) : [],
          health: rowBound.scaleHealth,
        }
      })
    }
  }).reverse()
}


const getRowColumnMapping = <Datum extends object, Key extends string>(
  chartRows: BubbleChartRow<Datum, Key>[],
  datumKey: TypedKeys<Datum, string | number>
) => {
  return chartRows.reduce( (acc, row) => {
    row.cells.forEach( cell => {
      if( !acc[cell.column] ){
        acc[cell.column] = {}
      }
      cell.data.forEach( d => {
        acc[cell.column][d[datumKey]] = row.key
      })
    })
    return acc
  }, {} as Record<Key, Partial<Record<any, string>>>)
}


const rowBoundsByKey = produceKeyedMapping(rowBounds, 'key')

type BubbleChangeDatum<Key extends string> = {
  key: Key
  datumId: string
  currentRow: string
  previousRow: string
  delta: number
}

const getChangeData = <Datum extends object, Key extends string>(
  chartRows: BubbleChartRow<Datum, Key>[],
  previousChartRows: BubbleChartRow<Datum, Key>[],
  datumKey: TypedKeys<Datum, string | number>,
): BubbleChangeDatum<Key>[] => {
  const currentRowMapping = getRowColumnMapping(chartRows, datumKey)
  const previousRowMapping = getRowColumnMapping(previousChartRows, datumKey)
  const combinedKeys = intersection(
    Object.keys(currentRowMapping) as Key[],
    Object.keys(previousRowMapping) as Key[],
  )
  const changeData = combinedKeys.reduce( (acc, key) => {
    const current = currentRowMapping[key]
    const previous = previousRowMapping[key]
    const combinedDatumKeys = intersection(Object.keys(current), Object.keys(previous))
    combinedDatumKeys.forEach( d => {
      const currentRow = current[d]
      const previousRow = previous[d]
      if( currentRow && previousRow && currentRow !== previousRow ){
        const delta = rowBoundsByKey[currentRow].health - rowBoundsByKey[previousRow].health
        acc.push({
          key,
          datumId: d,
          currentRow,
          previousRow,
          delta,
        })
      }
    })
    return acc
  }, [] as BubbleChangeDatum<Key>[])
  return changeData
}


type ChangeAnnotations<Key extends string> = Record<Key, Record<string, Partial<Record<'up' | 'down', string[]>>>>

const getChangeAnnotations = <Key extends string>(
  changeData: BubbleChangeDatum<Key>[]
): ChangeAnnotations<Key> => {
  const mapping = changeData.reduce( (acc, c) => {
    if( !acc[c.key] ){
      acc[c.key] = {}
    }
    const sourceRowKey = c.currentRow
    if( !acc[c.key][sourceRowKey] ){
      acc[c.key][sourceRowKey] = {}
    }
    const changeKey = c.delta > 0 ? 'up' : 'down'
    if( !acc[c.key][sourceRowKey][changeKey] ){
      acc[c.key][sourceRowKey][changeKey] = []
    }
    (acc[c.key][sourceRowKey][changeKey] as string[]).push(c.datumId)
    return acc
  }, {} as ChangeAnnotations<Key>)
  return mapping
}


type SizeProps = {
  width: number
  height: number
}

export function BubbleChartVisualisation<Datum extends object, Key extends string>({
  columns,
  data,
  width,
  height,
  cellMargin = 12,
  minCellSize = 30,
  fontSize = 14,
}: BubbleChartProps<Datum, Key> & SizeProps): JSX.Element {
  const theme = useAppTheme()
  const textColor = theme.palette.background.paper
  const colourScale = theme.chart.healthColourScale
  const columnWidth = width / columns.length
  const columnPercentageWidth = `${100 / columns.length}%`
  const rowHeight = height / 4
  const maxCellSize = rowHeight - (cellMargin * 2)
  const chartRows = getChartRows(columns, data)
  const dataCounts = flatten(chartRows.map( r => r.cells)).map( d => d.count )
  const domain = [
    Math.min(...dataCounts),
    Math.max(...dataCounts),
  ]
  const scale = scaleLinear({ domain, range: [minCellSize, maxCellSize] })
  return (
    <>
    <svg
      width={width}
      height={height}>
      { chartRows.map( (row, i) => (
        <>
          <g
            key={row.key}
            transform={`translate(0 ${i * rowHeight})`}>
            <line
              x1={0}
              x2='100%'
              y1={0}
              y2={0}
              stroke='currentColor'
              opacity={0.15} />
            { row.cells.map( (cell, j) => {
              if( !cell.count ){
                return null
              }
              const cx = (columnWidth * j) + (columnWidth / 2)
              const cy = rowHeight / 2
              return (
                <>
                  <circle
                    key={`${row.key}-${cell.column}`}
                    r={scale(cell.count) / 2}
                    fill={colourScale(row.health)}
                    cx={cx}
                    cy={cy} />
                  <text
                    x={cx}
                    y={cy}
                    dominantBaseline='central'
                    textAnchor='middle'
                    fill={textColor}
                    fontWeight={700}
                    fontSize={fontSize}>
                    { cell.count }
                  </text>
                </>
              )
            })}
          </g>
          { i === (chartRows.length - 1) && (
            <line
              x1={0}
              x2='100%'
              y1={rowHeight * (i + 1)}
              y2={rowHeight * (i + 1)}
              stroke='currentColor'
              opacity={0.15} />
          )}
          </>
      ))}
    </svg>
    <Box mt={3} display='flex' alignItems='center'>
      { columns.map( c => (
        <Box
          key={c.key}
          width={columnPercentageWidth}
          display='flex'
          justifyContent='center'>
          { c.label }
        </Box>
      ))}
    </Box>
    </>
  )
}

export function BubbleChartSVG<Datum extends object, Key extends string>(
  props: BubbleChartProps<Datum, Key> & Partial<SizeProps>
): JSX.Element {
  return (
    <ParentSize>
      {({ width, height }) => (
        <BubbleChartVisualisation
          width={width}
          height={height}
          {...props} />
      )}
    </ParentSize>
  )
}

export type BubbleProps<Datum extends object, Key extends string> = {
  columnKey: Key
  data: Datum[]
  size: string | number
  color: string
  classes: ClassNameMap<'count' | 'svg'>
  getPopoverContent?: (props: BubblePopoverProps<Datum, Key>) => React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>

export function Bubble<Datum extends object, Key extends string>({
  columnKey,
  data,
  size,
  color,
  classes,
  getPopoverContent,
  ...props
}: BubbleProps<Datum, Key>): JSX.Element {
  const ref = useRef<SVGSVGElement | null>(null)
  const [open, setOpen] = useState(false)
  return (
    <>
      <div {...props}>
        <svg
          ref={ref}
          height={size}
          className={classes.svg}
          viewBox='0 0 1 1'
          onClick={(): void => {
            if( getPopoverContent && !open ){
              setOpen(true)
            }
          }}>
          <circle
            cx={0.5}
            cy={0.5}
            r={0.5}
            fill={color} />
        </svg>
        <div className={classes.count}>
          { data.length }
        </div>
      </div>
      { open && getPopoverContent && (
        <Popover
          anchorEl={ref.current}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={open}
          onClose={(): void => {
            setOpen(false)
          }}>
          { getPopoverContent({ key: columnKey, data }) }
        </Popover>
      )}
    </>
  )
}


const useStyles = makeAppStyles<{ columns: number, rows: number }>( theme => ({
  chart: {
    maxWidth: '100%',
    height: 200,
    [theme.breakpoints.up('md')]: {
      height: 300,
    },
    [theme.breakpoints.up('lg')]: {
      height: 400,
    },
    [theme.breakpoints.up('xl')]: {
      height: 600,
    },
  },
  row: ({ rows }) => ({
    height: `${100 / rows}%`,
    display: 'flex',
    flexDirection: 'row',
    flexBasis: '100%',
    borderTop: `1px solid ${fade(theme.palette.text.primary, 0.1)}`,
    '&:last-of-type': {
      borderBottom: `1px solid ${fade(theme.palette.text.primary, 0.1)}`,
    },
  }),
  cell: ({ columns }) => ({
    width: `${100 / columns}%`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  }),
  cellContent: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {
    pointerEvents: 'none',
    userSelect: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.background.paper,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 14,
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  svg: {
    cursor: 'pointer',
    transition: theme.transitions.create('filter'),
    filter: 'drop-shadow(0 0 6px transparent)',
    '&:hover': {
      filter: `drop-shadow(0 0 6px rgba(0,0,0,${theme.palette.type === 'dark' ? 1 : 0.5}))`,
    },
  },
  changeArrowPath: {
    stroke: theme.palette.type === 'dark' ? '#999' : '#ccc'
  },
  changeArrowMarker: {
    fill: theme.palette.type === 'dark' ? '#999' : '#ccc'
  },
  changeLabel: {
    fill: theme.palette.background.paper,
    fontWeight: theme.typography.fontWeightBold,
  },
}))

export function BubbleChart<Datum extends object, Key extends string>({
  columns,
  data,
  previousData,
  datumKey,
  cellMargin = 20,
  minCellSize = 25,
  getPopoverContent,
}: BubbleChartProps<Datum, Key>): JSX.Element {
  const theme = useAppTheme()
  const colourScale = theme.chart.healthColourScale
  const chartRows = getChartRows(columns, data)
  const changeData = (
    !previousData ?
      null :
      getChangeData(chartRows, getChartRows(columns, previousData), datumKey)
  )
  const changeAnnotations = (
    !changeData ?
      null :
      getChangeAnnotations(changeData)
  )
  const maxCellSize = 100 - (cellMargin * 2)
  const changeAnnotationXOffset = 25
  const dataCounts = flatten(chartRows.map( r => r.cells)).map( d => d.count )
  const domain = [
    Math.min(...dataCounts),
    Math.max(...dataCounts),
  ]
  const scale = scaleLinear({ domain, range: [minCellSize, maxCellSize] })
  const classes = useStyles({
    columns: columns.length,
    rows: chartRows.length,
  })
  return (
    <>
    <div className={classes.chart}>
      { chartRows.map( row => (
        <div
          key={row.key}
          className={classes.row}>
          { row.cells.map( cell => {
            const size = `${scale(cell.count)}%`
            const cellChangeAnnotations = get(changeAnnotations, [cell.column, row.key], null)
            let annotationContent: React.ReactNode = null
            if( cellChangeAnnotations ){
              annotationContent = (
                <svg
                  width='100%'
                  height='100%'
                  viewBox='0 0 100 100'
                  pointerEvents='none'
                  style={{
                    position: 'absolute'
                  }}>
                  <defs>
                    <marker 
                      id='head'
                      orient='auto'
                      markerWidth='1.5'
                      markerHeight='2'
                      refX='0.1'
                      refY='1'>
                      <path className={classes.changeArrowMarker} d='M0,0 V2 L1,1 Z' />
                    </marker>
                  </defs>
                  { cellChangeAnnotations.up && (
                    <>
                    <path
                      className={classes.changeArrowPath}
                      markerEnd='url(#head)'
                      strokeWidth='20'
                      fill='none'
                      d={
                        `M${changeAnnotationXOffset},117.5, ${changeAnnotationXOffset} 100,100`
                      } />
                    <text
                      className={classes.changeLabel}
                      x={`${changeAnnotationXOffset}%`}
                      y='100%'
                      dominantBaseline='central'
                      textAnchor='middle'>
                      { cellChangeAnnotations.up.length }
                    </text>
                    </>
                  )}

                  { cellChangeAnnotations.down && (
                    <>
                    <path
                      className={classes.changeArrowPath}
                      markerEnd='url(#head)'
                      strokeWidth='20'
                      fill='none'
                      d={
                        `M${100 - changeAnnotationXOffset},-17.5, ${100 - changeAnnotationXOffset} 0,100`
                      } />
                    <text
                      className={classes.changeLabel}
                      x={`${100 - changeAnnotationXOffset}%`}
                      y='0%'
                      dominantBaseline='central'
                      textAnchor='middle'>
                      { cellChangeAnnotations.down.length }
                    </text>
                    </>
                  )}
                </svg>
              )
            }
            return (
              <div
                key={`${row.key}-${cell.column}`}
                className={classes.cell}>
                { !!cell.count && (
                  <Bubble
                    columnKey={cell.column}
                    className={classes.cellContent}
                    data={cell.data}
                    size={size}
                    color={colourScale(row.health)}
                    classes={classes}
                    getPopoverContent={getPopoverContent} />
                )}
                { annotationContent }
              </div>
            )
          })}
        </div>
      ))}
    </div>
    <Box mt={3} display='flex' alignItems='center'>
      { columns.map( c => (
        <div
          key={c.key}
          className={classes.cell}>
          { c.label }
        </div>
      ))}
    </Box>
    </>
  )
}

const rowHeights: Record<Breakpoint, number> = {
  xl: 100,
  lg: 80,
  md: 75,
  sm: 75,
  xs: 75,
}

const breakpoints: Breakpoint[] = [
  'xl', 'lg', 'md', 'sm', 'xs'
]


const useHorizontalStyles = makeAppStyles<{ columns: number, rows: number }>( theme => ({
  root: {
    display: 'flex',
  },
  columnLabels: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(3),
  },
  columnLabel: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    minWidth: 'min-content',
    ...(breakpoints.reduce( (acc, bp) => {
      acc[theme.breakpoints.down(bp)] = {
        height: rowHeights[bp],
      }
      return acc
    }, {} as Record<string, CreateCSSProperties>)),
  },
  chart: {
    flexGrow: 1,
  },
  row: {
    ...(breakpoints.reduce( (acc, bp) => {
      acc[theme.breakpoints.down(bp)] = {
        height: rowHeights[bp],
      }
      return acc
    }, {} as Record<string, CreateCSSProperties>)),
    display: 'flex',
    flexDirection: 'row',
    flexBasis: '100%',
  },
  cell: ({ rows }) => ({
    width: `${100 / rows}%`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    borderLeft: `1px solid ${fade(theme.palette.text.primary, 0.1)}`,
    '&:last-of-type': {
      borderRight: `1px solid ${fade(theme.palette.text.primary, 0.1)}`,
    },
  }),
  cellContent: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {
    pointerEvents: 'none',
    userSelect: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.background.paper,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 14,
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  svg: {
    cursor: 'pointer',
    transition: theme.transitions.create('filter'),
    filter: 'drop-shadow(0 0 6px transparent)',
    '&:hover': {
      filter: `drop-shadow(0 0 6px rgba(0,0,0,${theme.palette.type === 'dark' ? 1 : 0.5}))`,
    },
  },
  changeArrowPath: {
    stroke: theme.palette.type === 'dark' ? '#999' : '#ccc'
  },
  changeArrowMarker: {
    fill: theme.palette.type === 'dark' ? '#999' : '#ccc'
  },
  changeLabel: {
    fill: theme.palette.background.paper,
    fontWeight: theme.typography.fontWeightBold,
  },
}))


const pivotChartRows = <Datum extends object, Key extends string>(
  chartRows: BubbleChartRow<Datum, Key>[],
  columns: BubbleChartColumn<Key>[],
): BubbleChartRow<Datum, Key>[] => {
  const columnRowMapping = chartRows.reduce( (acc, row) => {
    row.cells.forEach( cell => {
      if( !acc[cell.column] ){
        acc[cell.column] = {}
      }
      if( !acc[cell.column][row.key] ){
        acc[cell.column][row.key] = cell
      }
    })
    return acc
  }, {} as Record<Key, Record<string, AggregatedBubbleChartDatum<Datum, Key>>>)
  return columns.reduce( (acc, c) => {
    const rowMapping = columnRowMapping[c.key]
    const cells: AggregatedBubbleChartDatum<Datum, Key>[] = rowBounds.map( row => {
      return {
        ...rowMapping[row.key],
        key: row.key,
      }
    })
    acc.push({
      key: c.key,
      label: c.label,
      cells,
      health: 0,
    })
    return acc
  }, [] as BubbleChartRow<Datum, Key>[])
}

export function BubbleChartHorizontal<Datum extends object, Key extends string>({
  columns,
  data,
  previousData,
  datumKey,
  cellMargin = 20,
  minCellSize = 25,
  getPopoverContent,
}: BubbleChartProps<Datum, Key>): JSX.Element {
  const theme = useAppTheme()
  const colourScale = theme.chart.healthColourScale
  const chartRows = getChartRows(columns, data)
  const changeData = (
    !previousData ?
      null :
      getChangeData(chartRows, getChartRows(columns, previousData), datumKey)
  )
  const changeAnnotations = (
    !changeData ?
      null :
      getChangeAnnotations(changeData)
  )
  const pivotedChartRows = pivotChartRows(chartRows, columns)
  const maxCellPercentage = 100 - (cellMargin * 2)
  const dataCounts = flatten(chartRows.map( r => r.cells)).map( d => d.count )
  const domain = [
    Math.min(...dataCounts),
    Math.max(...dataCounts),
  ]
  const scale = scaleLinear({ domain, range: [minCellSize / 100, maxCellPercentage / 100] })
  const classes = useHorizontalStyles({
    columns: columns.length,
    rows: chartRows.length,
  })
  const breakpoint = useBreakpoint()
  const rowHeight = rowHeights[breakpoint]
  const changeYOffset = rowHeight / 4
  return (
    <div className={classes.root}>
      <div className={classes.columnLabels}>
        { columns.map( c => (
          <div
            key={c.key}
            className={classes.columnLabel}>
            { c.label }
          </div>
        ))}
      </div>
      <div className={classes.chart}>
        <ParentSize>
          { ({ width }) => {
            const cellWidth = width / 4
            const changeXOffset = cellWidth / 8
            const maxBubbleSize = Math.min(rowHeight, cellWidth)
            return <>
            { pivotedChartRows.map( row => (
              <div
                key={row.key}
                className={classes.row}>
                { row.cells.map( cell => {
                  const size = scale(cell.count) * maxBubbleSize
                  const cellChangeAnnotations = get(changeAnnotations, [row.key, cell.key], null)
                  let annotationContent: React.ReactNode = null
                  if( cellChangeAnnotations ){
                    annotationContent = (
                      <svg
                        width={cellWidth}
                        height={rowHeight}
                        pointerEvents='none'
                        style={{
                          position: 'absolute'
                        }}>
                        <defs>
                          <marker 
                            id='head'
                            orient='auto'
                            markerWidth='1.5'
                            markerHeight='2'
                            refX='0.1'
                            refY='1'>
                            <path className={classes.changeArrowMarker} d='M0,0 V2 L1,1 Z' />
                          </marker>
                        </defs>
                        { cellChangeAnnotations.up && (
                        <>
                        <path
                          className={classes.changeArrowPath}
                          markerEnd='url(#head)'
                          strokeWidth='20'
                          fill='none'
                          d={
                            `M${-changeXOffset},${changeYOffset}, ${changeXOffset} ${changeYOffset},0`
                          } />
                        <text
                          className={classes.changeLabel}
                          x={0}
                          y={changeYOffset}
                          dominantBaseline='central'
                          textAnchor='middle'>
                          { cellChangeAnnotations.up.length }
                        </text>
                        </>
                        )}

                        { cellChangeAnnotations.down && (
                        <>
                        <path
                          className={classes.changeArrowPath}
                          markerEnd='url(#head)'
                          strokeWidth='20'
                          fill='none'
                          d={
                            `M${cellWidth + changeXOffset},${rowHeight - changeYOffset}, ${cellWidth - changeXOffset} ${rowHeight - changeYOffset},0`

                          } />
                          <text
                            className={classes.changeLabel}
                            x='100%'
                            y={rowHeight - changeYOffset}
                            dominantBaseline='central'
                            textAnchor='middle'>
                            { cellChangeAnnotations.down.length }
                          </text>
                        </>
                        )}
                      </svg>
                    )
                  }
                  return (
                    <div
                      key={`${row.key}-${cell.key}`}
                      className={classes.cell}>
                      { !!cell.count && (
                        <Bubble
                          columnKey={cell.column}
                          className={classes.cellContent}
                          data={cell.data}
                          size={size}
                          color={colourScale(cell.health)}
                          classes={classes}
                          getPopoverContent={getPopoverContent} />
                      )}
                      { annotationContent }
                    </div>
                  )
                })}
              </div>
            ))}
          </>
          }}
        </ParentSize>
      </div>
    </div>
  )
}
